import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Menu, Plus, LogOut, LayoutDashboard, MessageSquare, Heart, Bell, User,
  Sun, Moon, Monitor, Sparkles, UserCircle, Check,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetClose, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { useUnreadMessages } from "@/hooks/useUnreadMessages";
import { openAbayAI } from "@/lib/abayAiBus";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { LanguageSelector } from "@/components/layout/LanguageSelector";
import { Logo } from "@/components/brand/Logo";
import { NAV_ITEMS } from "@/data/properties";

/** Centered ABAY360 horizontal logo. `onDark` picks the white (dark-bg) mark. */
export const Brand = ({ onClick, onDark = false, className = "" }) => (
  <Link
    to="/"
    onClick={onClick}
    data-testid="header-logo-link"
    aria-label="ABAY360 home"
    className="inline-flex items-center rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] focus-visible:ring-offset-2"
  >
    <Logo
      variant={onDark ? "dark" : "light"}
      className={`h-auto w-[88px] transition-transform duration-200 hover:-translate-y-0.5 max-[360px]:w-[80px] min-[400px]:w-[112px] min-[440px]:w-[128px] md:w-[168px] lg:w-[188px] ${className}`}
    />
  </Link>
);

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(min-width: 768px)").matches,
  );
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user, profile, signOut } = useAuth();
  const { theme, mode, setMode } = useTheme();
  const { count: unreadMessages } = useUnreadMessages();
  const { t } = useTranslation();

  const displayName = profile?.full_name || user?.email || "";
  const initial = (displayName || "A").trim().charAt(0).toUpperCase();

  // Transparent header is a DESKTOP-ONLY treatment on the home hero.
  // Mobile is ALWAYS a solid header (white in light mode, dark navy in dark mode).
  const isHome = pathname === "/";
  const transparent = isHome && !scrolled && isDesktop;
  const onDark = transparent || theme === "dark";

  const handleSignOut = async () => {
    await signOut();
    toast.success("Signed out");
    navigate("/");
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(min-width: 768px)");
    const onChange = () => setIsDesktop(mq.matches);
    onChange();
    mq.addEventListener ? mq.addEventListener("change", onChange) : mq.addListener(onChange);
    return () => {
      mq.removeEventListener ? mq.removeEventListener("change", onChange) : mq.removeListener(onChange);
    };
  }, []);

  // Shared control (button) styling — consistent size & alignment across all icons.
  const ctrl =
    "inline-flex items-center justify-center rounded-lg transition-colors duration-200 outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] focus-visible:ring-offset-2 h-9 w-9 md:h-10 md:w-10";
  const ctrlColor = onDark
    ? "text-white hover:bg-white/15"
    : "text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-white/10";
  const glyph = "h-[18px] w-[18px] md:h-5 md:w-5";

  const ThemeIcon = mode === "system" ? Monitor : theme === "dark" ? Moon : Sun;

  const THEME_OPTIONS = [
    { key: "light", label: "Light", icon: Sun },
    { key: "dark", label: "Dark", icon: Moon },
    { key: "system", label: "System", icon: Monitor },
  ];

  const accountLinks = [
    { key: "profile", label: t("common.profile"), icon: User, to: "/account/profile", testid: "menu-profile" },
    { key: "dashboard", label: t("common.dashboard"), icon: LayoutDashboard, to: "/dashboard", testid: "menu-dashboard" },
    { key: "saved", label: t("common.saved"), icon: Heart, to: "/dashboard?tab=saved", testid: "menu-saved" },
    { key: "messages", label: t("common.messages"), icon: MessageSquare, to: "/messages", testid: "menu-messages", badge: unreadMessages },
  ];

  return (
    <motion.header
      initial={{ y: -24, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      data-testid="site-header"
      className={`sticky top-0 z-50 transition-[box-shadow,background-color,backdrop-filter,border-color] duration-200 ${
        transparent
          ? "border-b border-white/10 bg-gradient-to-b from-slate-950/86 via-slate-950/72 to-slate-950/45 backdrop-blur-[3px] shadow-[0_2px_16px_rgba(2,6,23,0.32)]"
          : "border-b border-slate-200 bg-white shadow-[0_1px_3px_rgba(2,6,23,0.08)] dark:border-white/10 dark:bg-[#0b1428]"
      }`}
      style={{ paddingTop: "env(safe-area-inset-top)" }}
    >
      <div className="relative mx-auto flex h-16 w-full max-w-[1440px] items-center justify-between px-3 sm:px-4 md:h-[76px] md:px-5 lg:h-20 lg:px-6">
        {/* ===================== LEFT — hamburger ===================== */}
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button
              type="button"
              data-testid="header-menu-button"
              aria-label="Open menu"
              className={`${ctrl} ${ctrlColor}`}
            >
              <Menu className={glyph} />
            </button>
          </SheetTrigger>
          <SheetContent side="left" className="flex w-[86%] max-w-sm flex-col p-0 pb-[calc(4rem+env(safe-area-inset-bottom))] lg:pb-0" data-testid="menu-drawer">
            <SheetHeader className="border-b border-border px-4 py-3">
              <SheetTitle className="text-left">
                <Brand onClick={() => setOpen(false)} />
              </SheetTitle>
            </SheetHeader>

            <div className="thin-scroll flex-1 overflow-y-auto px-3 py-4">
              {user && (
                <div className="mb-3 rounded-xl bg-muted px-3 py-2 text-sm text-muted-foreground">
                  {t("common.welcome")}, <span className="font-medium text-foreground">{displayName}</span>
                </div>
              )}

              {/* Browse */}
              <p data-testid="menu-browse-section" className="px-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{t("common.browse")}</p>
              <nav className="mt-1 flex flex-col gap-0.5">
                {NAV_ITEMS.map((item) => (
                  <SheetClose asChild key={item.to}>
                    <NavLink
                      to={item.to}
                      data-testid={`menu-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className={({ isActive }) =>
                        `rounded-xl px-3 py-2.5 text-[15px] font-medium transition-colors duration-200 ${
                          isActive ? "bg-[#1E3A8A]/10 text-[#1E3A8A] dark:text-[#7AA0FF]" : "text-foreground hover:bg-muted"
                        }`
                      }
                    >
                      {item.label}
                    </NavLink>
                  </SheetClose>
                ))}
                <SheetClose asChild>
                  <button
                    type="button"
                    data-testid="menu-nav-abay-ai"
                    onClick={() => openAbayAI()}
                    className="flex items-center gap-2 rounded-xl px-3 py-2.5 text-left text-[15px] font-medium text-foreground transition-colors duration-200 hover:bg-muted"
                  >
                    <Sparkles className="h-4 w-4 text-[#10B981]" /> {t("nav.abayAI")}
                    <span className="ml-1 rounded-full bg-[#10B981]/12 px-1.5 py-0.5 text-[10px] font-semibold text-[#0E9F6E]">{t("common.new")}</span>
                  </button>
                </SheetClose>
              </nav>

              {/* Account */}
              <p data-testid="menu-account-section" className="mt-5 px-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{t("common.account")}</p>
              <div className="mt-1 flex flex-col gap-0.5">
                {user ? (
                  <>
                    {accountLinks.map((a) => (
                      <SheetClose asChild key={a.key}>
                        <button
                          type="button"
                          data-testid={a.testid}
                          onClick={() => navigate(a.to)}
                          className="flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-[15px] font-medium text-foreground transition-colors duration-200 hover:bg-muted"
                        >
                          <a.icon className="h-5 w-5 text-muted-foreground" /> {a.label}
                          {a.badge > 0 && (
                            <span className="ml-auto flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#10B981] px-1 text-[10px] font-semibold text-white">
                              {a.badge > 9 ? "9+" : a.badge}
                            </span>
                          )}
                        </button>
                      </SheetClose>
                    ))}
                    <SheetClose asChild>
                      <button
                        type="button"
                        data-testid="menu-logout-button"
                        onClick={handleSignOut}
                        className="flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-[15px] font-medium text-[#DC2626] transition-colors duration-200 hover:bg-[#DC2626]/10"
                      >
                        <LogOut className="h-5 w-5" /> {t("common.logout")}
                      </button>
                    </SheetClose>
                  </>
                ) : (
                  <>
                    <SheetClose asChild>
                      <button type="button" data-testid="menu-login" onClick={() => navigate("/login")} className="flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-[15px] font-medium text-foreground transition-colors duration-200 hover:bg-muted">
                        <User className="h-5 w-5 text-muted-foreground" /> {t("common.login")}
                      </button>
                    </SheetClose>
                    <SheetClose asChild>
                      <button type="button" data-testid="menu-signup" onClick={() => navigate("/signup")} className="flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-[15px] font-medium text-foreground transition-colors duration-200 hover:bg-muted">
                        <UserCircle className="h-5 w-5 text-muted-foreground" /> {t("common.signup")}
                      </button>
                    </SheetClose>
                  </>
                )}
              </div>

              {/* Appearance — mobile theme control (desktop uses the header theme toggle) */}
              <p data-testid="menu-appearance-section" className="mt-5 px-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground md:hidden">{t("common.appearance") !== "common.appearance" ? t("common.appearance") : "Appearance"}</p>
              <div className="mt-1 grid grid-cols-3 gap-1.5 px-1 md:hidden">
                {THEME_OPTIONS.map((o) => (
                  <button
                    key={o.key}
                    type="button"
                    data-testid={`drawer-theme-${o.key}`}
                    onClick={() => setMode(o.key)}
                    className={`flex flex-col items-center gap-1.5 rounded-xl border px-2 py-2.5 text-xs font-medium transition-colors duration-200 ${
                      mode === o.key
                        ? "border-[#1E3A8A] bg-[#1E3A8A]/10 text-[#1E3A8A] dark:border-[#7AA0FF] dark:text-[#7AA0FF]"
                        : "border-border text-foreground hover:bg-muted"
                    }`}
                  >
                    <o.icon className="h-4 w-4" /> {o.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Sticky bottom CTA */}
            <div className="border-t border-border p-3">
              <SheetClose asChild>
                <Button onClick={() => navigate("/list-property")} data-testid="menu-list-property" className="w-full rounded-xl bg-[#10B981] text-white hover:bg-[#0E9F6E]">
                  <Plus className="mr-1 h-4 w-4" /> {t("common.listProperty")}
                </Button>
              </SheetClose>
            </div>
          </SheetContent>
        </Sheet>

        {/* ===================== CENTER — logo (perfectly centered, never shifts) ===================== */}
        <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          <div className="pointer-events-auto">
            <Brand onDark={onDark} />
          </div>
        </div>

        {/* ===================== RIGHT — [Theme (desktop)] | Language | Notifications | Profile =====================
            On mobile the standalone Theme toggle is intentionally moved into the hamburger drawer
            (Appearance section) so the centered logo never collides with the right cluster. */}
        <div className="flex items-center gap-1 sm:gap-1.5 md:gap-1.5">
          {/* Theme — desktop only (mobile uses the drawer Appearance section) */}
          <div className="hidden md:block">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  data-testid="header-theme-toggle"
                  aria-label="Change theme"
                  className={`${ctrl} ${ctrlColor}`}
                >
                  <ThemeIcon className={glyph} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-40" data-testid="theme-menu">
                {THEME_OPTIONS.map((o) => (
                  <DropdownMenuItem
                    key={o.key}
                    data-testid={`theme-option-${o.key}`}
                    onClick={() => setMode(o.key)}
                    className="flex items-center justify-between gap-2"
                  >
                    <span className="flex items-center gap-2"><o.icon className="h-4 w-4" /> {o.label}</span>
                    {mode === o.key && <Check className="h-4 w-4 text-[#10B981]" />}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Language — single control 🌐 EN ▾ */}
          <LanguageSelector onDark={onDark} />

          {/* Notifications */}
          <button
            type="button"
            data-testid="header-notifications-button"
            aria-label="Notifications"
            onClick={() => navigate(user ? "/dashboard?tab=alerts" : "/login")}
            className={`relative ${ctrl} ${ctrlColor}`}
          >
            <Bell className={glyph} />
            {unreadMessages > 0 && (
              <span className="absolute right-1 top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[#10B981] px-0.5 text-[9px] font-bold text-white">
                {unreadMessages > 9 ? "9+" : unreadMessages}
              </span>
            )}
          </button>

          {/* Profile */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                data-testid="header-profile-menu"
                aria-label="Account menu"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] focus-visible:ring-offset-2"
              >
                {user ? (
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#1E3A8A] text-sm font-semibold text-white">
                    {initial}
                  </span>
                ) : (
                  <span className={`flex h-9 w-9 items-center justify-center rounded-full ${onDark ? "bg-white/20 text-white ring-1 ring-white/25" : "bg-slate-100 text-slate-700 dark:bg-white/10 dark:text-slate-200"}`}>
                    <User className="h-5 w-5" />
                  </span>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56" data-testid="header-profile-dropdown">
              {user ? (
                <>
                  <DropdownMenuLabel className="truncate">{displayName}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {accountLinks.map((a) => (
                    <DropdownMenuItem key={a.key} data-testid={`profile-menu-${a.key}`} onClick={() => navigate(a.to)}>
                      <a.icon className="mr-2 h-4 w-4" /> {a.label}
                      {a.badge > 0 && (
                        <span className="ml-auto flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#10B981] px-1 text-[10px] font-semibold text-white">
                          {a.badge > 9 ? "9+" : a.badge}
                        </span>
                      )}
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuItem data-testid="profile-menu-list-property" onClick={() => navigate("/list-property")}>
                    <Plus className="mr-2 h-4 w-4" /> {t("common.listProperty")}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="profile-menu-logout" onClick={handleSignOut} className="text-[#DC2626] focus:text-[#DC2626]">
                    <LogOut className="mr-2 h-4 w-4" /> {t("common.logout")}
                  </DropdownMenuItem>
                </>
              ) : (
                <>
                  <DropdownMenuLabel>{t("common.account")}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="profile-menu-login" onClick={() => navigate("/login")}>
                    <User className="mr-2 h-4 w-4" /> {t("common.login")}
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="profile-menu-signup" onClick={() => navigate("/signup")}>
                    <UserCircle className="mr-2 h-4 w-4" /> {t("common.signup")}
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </motion.header>
  );
};
